import { useRef, useState } from "react";
import { api } from "./api";
import type { ImportJob } from "./import-progress";

interface PreparedFiles {
  id: string;
  files: Array<{ path: string; size: number }>;
  markdownCount: number;
  imageCount: number;
}

export function FileImport({ disabled, ready, beforeGenerate, onStarted }: { disabled: boolean; ready: boolean; beforeGenerate: () => Promise<void>; onStarted: (job: ImportJob) => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [prepared, setPrepared] = useState<PreparedFiles | null>(null);
  const [working, setWorking] = useState("");
  const [error, setError] = useState("");
  const select = (list: FileList | null) => { setFiles(Array.from(list || [])); setPrepared(null); setError(""); };
  async function read() {
    setWorking("正在上传并展开文件..."); setError("");
    try {
      const input = await Promise.all(files.map(async (file) => ({ path: file.webkitRelativePath || file.name, base64: await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",", 2)[1]);
        reader.onerror = () => reject(new Error(`文件读取失败: ${file.name}`));
        reader.readAsDataURL(file);
      }) })));
      setPrepared(await api<PreparedFiles>("/api/import/files/prepare", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ files: input }) }));
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setWorking(""); }
  }
  async function generate() {
    if (!prepared) return;
    setWorking("正在开始生成..."); setError("");
    try {
      await beforeGenerate();
      const result = await api<{ job: ImportJob }>("/api/import/files", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: prepared.id }) });
      onStarted(result.job);
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setWorking(""); }
  }
  return <section className="card">
    <div className="card-header"><div><h2>本地文件</h2><p className="hint">选择 MD、文件夹或压缩包. 压缩包展开后, 先确认所有读取到的文件.</p></div></div>
    <div className="card-pad">
      <input ref={fileInput} type="file" multiple hidden accept=".md,.markdown,.zip,.7z,.rar,.tar,.gz,.tgz,.bz2,.xz,image/*" onChange={(event) => select(event.target.files)}/>
      <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: "", directory: "" }} onChange={(event) => select(event.target.files)}/>
      <div className="row-actions"><button className="btn" disabled={disabled || Boolean(working)} onClick={() => fileInput.current?.click()}>选择文件或压缩包</button><button className="btn" disabled={disabled || Boolean(working)} onClick={() => folderInput.current?.click()}>选择文件夹</button><button className="btn btn-primary" disabled={disabled || Boolean(working) || !files.length} onClick={() => void read()}>读取文件</button><span className="hint">已选择 {files.length} 个文件</span></div>
      {working && <p role="status"><span className="spinner"/>{working}</p>}
      {error && <p className="error-text" role="alert">{error}</p>}
      {prepared && <><p className="hint">已读取 {prepared.files.length} 个文件 · {prepared.markdownCount} 篇 MD · {prepared.imageCount} 张图片</p><div className="table-scroll"><table><thead><tr><th>最终文件路径</th><th>大小</th></tr></thead><tbody>{prepared.files.map((file) => <tr key={file.path}><td>{file.path}</td><td>{file.size < 1024 ? `${file.size} B` : `${(file.size / 1024).toFixed(1)} KB`}</td></tr>)}</tbody></table></div><div className="import-footer"><p className="hint">确认文件后, AI 会识别文章、语言和素材, 补充本地待传文章并独立校验.</p><button className="btn btn-primary" disabled={!ready || disabled || Boolean(working)} onClick={() => void generate()}>确认文件并生成文章</button></div></>}
    </div>
  </section>;
}
